import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBundleMonitor } from '../hooks/useBundleMonitor';
import { useAuth } from '../contexts/AuthContext';
import { useToastContext } from '../contexts/ToastContext';
import PushNotificationManager, { NotificationPreferences } from '../utils/pushNotifications';
import Button from './ui/button';

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const [analyzing, setAnalyzing] = useState(false);
  const { analyzeNow, getBundleHistory, isEnabled } = useBundleMonitor();
  const { logout, token } = useAuth();
  const { showSuccess, showError } = useToastContext();
  
  // Notification preferences state
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    email: true,
    push: false,
    morningTime: '09:00',
    eveningTime: '18:00',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  });
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const pushManager = PushNotificationManager.getInstance();

  // Load notification preferences on component mount
  useEffect(() => {
    const loadPreferences = async () => {
      if (!token) return;
      
      try {
        await pushManager.initialize();
        const [prefs, subscribed] = await Promise.all([
          pushManager.getPreferences(token),
          pushManager.isSubscribed()
        ]);
        
        if (prefs) {
          setPreferences(prefs);
        }
        setIsSubscribed(subscribed);
      } catch (error) {
        console.error('Error loading notification preferences:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPreferences();
  }, [token, pushManager]);

  const handleBundleAnalysis = async () => {
    setAnalyzing(true);
    try {
      await analyzeNow();
    } finally {
      setAnalyzing(false);
    }
  };

  const handleNotificationToggle = async (type: 'email' | 'push', enabled: boolean) => {
    if (!token) return;

    try {
      if (type === 'push') {
        if (enabled && !isSubscribed) {
          const success = await pushManager.subscribe(token);
          if (success) {
            setIsSubscribed(true);
            showSuccess('Push notifications enabled successfully!');
          } else {
            showError('Failed to enable push notifications');
            return;
          }
        } else if (!enabled && isSubscribed) {
          const success = await pushManager.unsubscribe(token);
          if (success) {
            setIsSubscribed(false);
            showSuccess('Push notifications disabled');
          } else {
            showError('Failed to disable push notifications');
            return;
          }
        }
      }

      const updatedPrefs = { ...preferences, [type]: enabled };
      const success = await pushManager.updatePreferences(updatedPrefs, token);
      
      if (success) {
        setPreferences(updatedPrefs);
        showSuccess('Notification preferences updated!');
      } else {
        showError('Failed to update preferences');
      }
    } catch (error) {
      console.error('Error updating notification preference:', error);
      showError('Failed to update notification settings');
    }
  };

  const handleTimeChange = async (type: 'morningTime' | 'eveningTime', time: string) => {
    if (!token) return;

    const updatedPrefs = { ...preferences, [type]: time };
    const success = await pushManager.updatePreferences(updatedPrefs, token);
    
    if (success) {
      setPreferences(updatedPrefs);
      showSuccess('Notification time updated!');
    } else {
      showError('Failed to update notification time');
    }
  };

  const handleTestNotification = async () => {
    if (!token || !preferences.push || !isSubscribed) {
      showError('Push notifications are not enabled');
      return;
    }

    try {
      const success = await pushManager.sendTestNotification(token);
      if (success) {
        showSuccess('Test notification sent!');
      } else {
        showError('Failed to send test notification');
      }
    } catch (error) {
      console.error('Error sending test notification:', error);
      showError('Failed to send test notification');
    }
  };

  const handleLogout = () => {
    logout();
    window.scrollTo(0, 0);
    navigate('/');
  };

  return (
    <div className="w-full max-w-2xl mx-auto px-4 py-6 md:px-2 md:py-4 flex flex-col gap-6">
      <h2 className="text-2xl font-bold text-text-primary mb-2">Settings</h2>
      
      {/* Analytics Access */}
      <div className="bg-white rounded-xl shadow p-6 md:p-6 flex flex-col gap-4">
        <h3 className="text-lg font-semibold text-text-primary mb-2">Analytics & Insights</h3>
        <p className="text-text-secondary text-sm mb-4">
          View detailed analytics about your momentum, check-ins, and progress patterns.
        </p>
        <Button 
          onClick={() => navigate('/analytics')}
          ambient={true}
          className="flex items-center gap-2 px-4 py-4 md:py-3 text-sm w-full md:w-max min-h-[48px] justify-center md:justify-start"
        >
          <span className="text-lg">📊</span>
          View Analytics Dashboard
        </Button>
      </div>
      
      {/* Notification Settings */}
      <div className="bg-white rounded-xl shadow p-6 md:p-6 flex flex-col gap-5 md:gap-4">
        <h3 className="text-lg font-semibold text-text-primary mb-2">Notification Preferences</h3>
        
        {loading ? (
          <div className="text-text-secondary">Loading notification settings...</div>
        ) : (
          <>
            {/* Email Notifications */}
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <label className="text-text-primary font-medium text-base md:text-sm">Email Notifications</label>
                <span className="text-text-secondary text-sm">Receive check-in reminders via email</span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.email}
                  onChange={(e) => handleNotificationToggle('email', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-rose-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-rose-500"></div>
              </label>
            </div>

            {/* Push Notifications */}
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <label className="text-text-primary font-medium text-base md:text-sm">Push Notifications</label>
                <span className="text-text-secondary text-sm">Receive check-in reminders as browser notifications</span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.push && isSubscribed}
                  onChange={(e) => handleNotificationToggle('push', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-rose-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-rose-500"></div>
              </label>
            </div>

            {/* Morning Check-in Time */}
            <div className="flex flex-col gap-2 md:gap-1">
              <label className="text-text-secondary font-medium text-base md:text-sm">Morning Check-In Time</label>
              <input
                type="time"
                value={preferences.morningTime}
                onChange={(e) => handleTimeChange('morningTime', e.target.value)}
                className="border border-gray-300 rounded-lg px-4 py-3 md:px-3 md:py-2 text-text-primary text-base md:text-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
              />
            </div>

            {/* Evening Check-in Time */}
            <div className="flex flex-col gap-2 md:gap-1">
              <label className="text-text-secondary font-medium text-base md:text-sm">Evening Check-In Time</label>
              <input
                type="time"
                value={preferences.eveningTime}
                onChange={(e) => handleTimeChange('eveningTime', e.target.value)}
                className="border border-gray-300 rounded-lg px-4 py-3 md:px-3 md:py-2 text-text-primary text-base md:text-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
              />
            </div>

            {/* Test Notification Button */}
            {preferences.push && isSubscribed && (
              <Button
                onClick={handleTestNotification}
                ambient={true}
                className="mt-2 px-4 py-3 md:px-3 md:py-2 text-sm w-full md:w-max min-h-[48px] md:min-h-[36px]"
              >
                <span className="text-lg mr-2">🔔</span>
                Send Test Notification
              </Button>
            )}
          </>
        )}
      </div>

      {/* Logout Section */}
      <div className="bg-white rounded-xl shadow p-6 md:p-6 flex flex-col gap-4">
        <h3 className="text-lg font-semibold text-text-primary mb-2">Account</h3>
        <p className="text-text-secondary text-sm mb-4">
          Sign out of your ThinkMate account and return to the login page.
        </p>
        <Button 
          onClick={handleLogout}
          className="flex items-center gap-2 px-4 py-4 md:py-3 text-sm w-full md:w-max min-h-[48px] justify-center md:justify-start bg-red-600 hover:bg-red-700 text-white border-red-600 hover:border-red-700"
        >
          <span className="text-lg">🚪</span>
          Sign Out
        </Button>
      </div>

      {/* Development Tools */}
      {process.env.NODE_ENV === 'development' && (
        <div className="bg-white rounded-xl shadow p-6 md:p-6 flex flex-col gap-4">
          <h3 className="text-lg font-semibold text-text-primary mb-2">Development Tools</h3>
          <p className="text-text-secondary text-sm mb-4">
            Monitor application performance and bundle size in development.
          </p>
          
          <div className="flex flex-col gap-3">
            <Button 
              onClick={handleBundleAnalysis}
              disabled={analyzing || !isEnabled}
              ambient={true}
              className="flex items-center gap-2 px-4 py-4 md:py-3 text-sm w-full md:w-max min-h-[48px] justify-center md:justify-start disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="text-lg">📦</span>
              {analyzing ? 'Analyzing Bundle...' : 'Analyze Bundle Size'}
            </Button>
            
            {isEnabled && getBundleHistory().length > 0 && (
              <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                <div className="text-sm font-medium text-text-primary mb-2">Recent Bundle Stats</div>
                <div className="text-xs text-text-secondary">
                  Last analysis: {new Date(getBundleHistory().slice(-1)[0]?.timestamp || 0).toLocaleDateString()}
                </div>
                <div className="text-xs text-text-secondary">
                  Bundle history: {getBundleHistory().length} entries
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings; 